import { AppColors } from "@/styles/colors";
import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  MenuOpenChangeDetails,
  Portal,
  Text,
} from "@chakra-ui/react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { LightMode } from "../ui/color-mode";
import { SelectComponent } from "../select/Select";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { TextArea } from "../text-area/TextArea";
import { getMotivos } from "@/services/aux-entities.service";
import { usePolling } from "@/hooks/usePolling";
import { isEmpty } from "@/utils/query-string";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleAccept?: (event?: FieldValues) => Promise<void>;
  loadingAccept?: boolean;
  idTipoSolicitud?: number;
}

export const NeedInfoModal = ({
  open,
  setOpen,
  handleAccept,
  loadingAccept,
  idTipoSolicitud = 1,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const [loadingMotivos, setLoadingMotivos] = useState<boolean>(false);

  const [motivos, setMotivos] = useState<any[]>([]);

  const fetchMotivos = async () => {
    setLoadingMotivos(true);
    const response = await getMotivos({
      esRechazo: false,
      idTipoSolicitud,
    });

    setMotivos(response ?? []);

    setLoadingMotivos(false);
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) {
      fetchMotivos();
    }
  }, [isActive]);

  const {
    control,
    register,
    reset,
    formState: { errors },
    getValues,
  } = useForm();

  const message_motive = useWatch({
    name: "message_motive",
    control,
  });

  const messageLabel = motivos?.find(
    (el) => el.id === message_motive?.[0],
  )?.descripcion;

  const handleSetOpen = (e: MenuOpenChangeDetails) => {
    reset({
      message_motive: [],
      request_detail: undefined,
    });
    setOpen(e.open);
  };

  const submitForm = async () => {
    await handleAccept?.({
      ...getValues(),
      request_detail:
        messageLabel === "Otro" ? getValues().request_detail : messageLabel,
    });

    reset({
      message_motive: [],
      request_detail: "",
    });
  };

  usePolling({
    callback: () => fetchMotivos(),
    delay: 10000,
    isActive,
  });

  const requestDetail = useWatch({
    name: "request_detail",
    control,
  });

  const isOtherMissingDetail =
    messageLabel === "Otro" && isEmpty(requestDetail);

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={handleSetOpen}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content data-testid="confirm-modal">
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title
                  fontWeight={600}
                  color={AppColors.GRAY_TEXT}
                  fontSize="18px"
                >
                  Requiere información
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body paddingX="24px" paddingY="8px" width="100%">
                <Flex flexDir="column" gap="10px">
                  <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                    Comente la información necesaria al cliente
                  </Text>

                  <SelectComponent
                    control={control}
                    name="message_motive"
                    placeholder="Seleccione una opción"
                    loading={loadingMotivos}
                    disabled={loadingMotivos}
                    items={motivos?.map((motivo) => ({
                      label: motivo.descripcion,
                      value: motivo.id,
                    }))}
                    fullWidth
                  />

                  {messageLabel === "Otro" && (
                    <TextArea
                      errors={errors}
                      inputName="request_detail"
                      register={register}
                      placeholder="Ingresar una descripción de la solicitud"
                      maxLength={50}
                    />
                  )}
                </Flex>
              </Dialog.Body>
              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={submitForm}
                  loading={loadingAccept}
                  disabled={
                    loadingAccept ||
                    !message_motive?.length ||
                    isOtherMissingDetail
                  }
                >
                  Aceptar
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
